import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send, CheckCircle2, AlertCircle, Clock,
  Star, User, ShieldCheck, Loader2,
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { getAppraisalDetails, submitAppraisalReview, saveAppraisalDraft } from '@/services/performanceService';
import { draftFromForm, formFromDraft } from '../shared/reviewDraft';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/contexts/RoleAccessContext';
import { useReviewsListPath } from '@/contexts/TeamScopeContext';
import { createCanHelpers } from '@/utils/rbac';
import {
  AppraisalHeader,
  EmployeeInfoCard,
  StatusBanner,
  SectionCard,
  QuestionEditor,
  QuestionDisplay,
  FinalCommentCard,
} from '../shared';

const STEPS = ["Self Review", "Approval Review", "Employee Comment", "Admin Review", "Completed"];

function ManagerAppraisalEditor() {
  const { user } = useAuth();
  const { can } = usePermission();
  // Back to the list this was opened from: Team reviews or Performance → Reviews.
  const reviewsPath = useReviewsListPath();
  const { canApprove } = createCanHelpers(can);
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [appraisal, setAppraisal] = useState(null);
  const [template, setTemplate] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // Form state
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [starScores, setStarScores] = useState({});

  useEffect(() => {
    if (!isNew) loadAppraisal();
  }, [id]);

  useEffect(() =>{
    return () =>{
      setRatings({});
      setComments({});
      setStarScores({});
    }
  }, []);

  const loadAppraisal = async () => {
    try {
      const response = await getAppraisalDetails(id);
      const appraisalData = response.data;

      setAppraisal({
        id: appraisalData._id,
        cycleName: appraisalData.cycle_name,
        employeeName: appraisalData.employee_name,
        employeeId: appraisalData?.employee_id?.employee_id,
        department: appraisalData.department,
        status: appraisalData.status,
        selfReview: appraisalData.self_review,
        approvalReview: appraisalData.manager_review,
        employeeComments: appraisalData.employee_comments,
        adminReview: appraisalData.hr_review,
        hrSignOff: appraisalData.hr_sign_off,
        templateId: appraisalData.template_id?._id,
        canSubmitManagerReview: appraisalData.can_submit_manager_review === true,
        canComplete: appraisalData.can_complete === true,
        cancelledAt: appraisalData.cancelled_at,
        cancelledBy: appraisalData.cancelled_by,
        cancellationReason: appraisalData.cancellation_reason,
      });

      // Set employee object from populated data
      if (appraisalData.employee_id && typeof appraisalData.employee_id === 'object') {
        setEmployee({
          employee_id: appraisalData.employee_id.employee_id,
          name: appraisalData.employee_id.name,
          designation: appraisalData.employee_id.designation,
          department: appraisalData.employee_id.department,
          manager: appraisalData.employee_id.manager || '—',
          joinDate: appraisalData.employee_id.date_of_joining 
          ? new Date(appraisalData.employee_id.date_of_joining).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        })
        : '—'
        });
      }

      setTemplate(appraisalData.template_id);

      if (appraisalData.my_draft) {
        const form = formFromDraft(appraisalData.my_draft, appraisalData.template_id?.sections);
        setRatings(form.ratings);
        setComments(form.comments);
        setStarScores(form.starScores);
        setDraftSavedAt(appraisalData.my_draft.updatedAt);
      }
    } catch {
      toast.error("Failed to load appraisal");
    } finally {
      setLoading(false);
    }
  };

  const ratingOptions = template?.ratingOptions ? [...template.ratingOptions].reverse() : ["Exceeds Expectations", "Meets Expectations", "Needs Improvement", "Unacceptable"];
  const sections = template?.sections || [];
  const allQuestionIds = sections.flatMap(s => (s.questions || []).map(q => q.id));
  const answeredCount = Object.keys(ratings).filter(k => allQuestionIds.includes(k) && ratings[k]).length;
  const progressPct = allQuestionIds.length > 0 ? Math.round((answeredCount / allQuestionIds.length) * 100) : 0;

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const response = await saveAppraisalDraft(id, draftFromForm({ ratings, comments, starScores }, allQuestionIds));
      setDraftSavedAt(response.data?.updatedAt || new Date());
      toast.success("Draft saved. Only you can see it until you submit.");
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || err.message || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitHRReview = async () => {
    if (!canApprove("performance_management", true)) return;

    // Validation: Ensure all questions have been rated
    if (!allQuestionIds.every(qId => ratings[qId])) {
      toast.error("Please rate all questions before submitting");
      return;
    }

    const score = answeredCount > 0
      ? parseFloat((Object.values(starScores).reduce((s, v) => s + v, 0) / Object.values(starScores).length).toFixed(1))
      : 0;

    setSubmitting(true);
    try {
      const responses = allQuestionIds.map(qId => ({
        section_id: sections.find(s => s.questions?.find(q => q.id === qId))?.id,
        section_title: sections.find(s => s.questions?.find(q => q.id === qId))?.title,
        question_id: qId,
        question_text: sections.flatMap(s => s.questions || []).find(q => q.id === qId)?.text,
        rating: starScores[qId] || 0,
        rating_label: ratings[qId],
        comment: comments[qId] || "",
        star_score: starScores[qId] || 0
      }));
      const overallComment = comments.overallComment?.trim() || "";

      await submitAppraisalReview(id, 'manager', {
        responses,
        overallComment,
        overallRating: score
      });

      setAppraisal(prev => ({
        ...prev,
        status: "Employee Comment",
        approvalReview: { responses, overall_comment: overallComment, overallScore: score, submittedAt: new Date() }
      }));
      setRatings({});
      setComments({});
      setStarScores({});

      toast.success("Approval review submitted! Waiting for employee comment.");
      setTimeout(() => navigate(reviewsPath), 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || err.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteAppraisal = async () => {
    if (!canApprove("performance_management", true)) return;

    setSubmitting(true);
    try {
      const responses = sections.flatMap(section =>
        (section.questions || [])
          .filter(q => comments[q.id]?.trim())
          .map(q => ({
            section_id: section.id,
            section_title: section.title,
            question_id: q.id,
            question_text: q.text,
            comment: comments[q.id],
          }))
      );

      const response = await submitAppraisalReview(id, 'complete', {
        responses,
        overallComment: comments.overallComment?.trim() || "",
      });
      setAppraisal(prev => ({ ...prev, status: "Completed", adminReview: response.data?.hr_review || prev.adminReview }));
      setComments({});
      toast.success("Appraisal completed successfully!");
      setTimeout(() => navigate(reviewsPath), 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!appraisal) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <p className="text-lg">Appraisal not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(reviewsPath)}>Back</Button>
      </div>
    );
  }

  const status = appraisal.status;
  const isCompleted = status === "Completed";
  const isCancelled = status === "Cancelled";

  const getSubmitAction = () => {
    switch (status) {
      case "Approval Review":
        return appraisal.canSubmitManagerReview
          ? { label: "Submit Approval Review", icon: Send, handler: handleSubmitHRReview, color: "bg-primary hover:bg-primary/90" }
          : null;
      case "Admin Review":
        return appraisal.canComplete
          ? { label: "Complete Appraisal", icon: CheckCircle2, handler: handleCompleteAppraisal, color: "bg-primary hover:bg-primary/90" }
          : null;
      default: return null;
    }
  };

  const submitAction = getSubmitAction();

  const handleQuestionChange = (qId, field, value) => {
    if (field === 'rating') setRatings(prev => ({ ...prev, [qId]: value }));
    if (field === 'starScore') setStarScores(prev => ({ ...prev, [qId]: value }));
    if (field === 'comment') setComments(prev => ({ ...prev, [qId]: value }));
  };

  return (
    <div className="max-w-7xl mx-auto pb-20">
      <AppraisalHeader
        appraisal={appraisal}
        status={status}
        submitAction={submitAction}
        draftAction={submitAction ? { handler: handleSaveDraft, saving: savingDraft, savedAt: draftSavedAt } : null}
        isCompleted={isCompleted}
        isEmployeeView={false}
        onBack={() => navigate(-1)}
        submitting={submitting}
        STEPS={STEPS}
        template={template}
        employee={employee}
        canApprove={canApprove("performance_management")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3">
          <div className="lg:sticky lg:top-0.5">
            <EmployeeInfoCard employee={employee} appraisal={appraisal} status={status} />
          </div>
        </div>

        <div className="lg:col-span-9 space-y-8">

          {isCancelled && (
            <>
              <Card className="border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/20">
                <CardContent className="py-4 sm:py-6 flex items-start gap-3 sm:gap-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-amber-600 h-6 w-6 sm:h-8 sm:w-8 mt-0.5 shrink-0" />
                    <div  className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold leading-tight text-amber-700 dark:text-amber-300">Appraisal Cancelled</h3>
                      <p className="text-xs sm:text-sm leading-relaxed text-amber-700 dark:text-amber-300 mt-1">
                        This appraisal is locked and can no longer be updated.
                      </p>
                      {appraisal.cancellationReason && (
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                          <span className="leading-relaxed truncate">Reason : </span> {appraisal.cancellationReason}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(
                      r => r.question_id === question.id || r.questionId === question.id
                    );
                    const mgrResp = appraisal.approvalReview?.responses?.find(
                      r => r.question_id === question.id || r.questionId === question.id
                    );

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={mgrResp}
                        viewMode="manager"
                      />
                    );
                  })}
                </SectionCard>
              ))}

              <FinalCommentCard title="Manager's Final Comment" comment={appraisal.approvalReview?.overall_comment} />
            </>
          )}


          {status === "Self Review" && sections.length > 0 && (
            <>
              <StatusBanner
                title="Waiting for Employee Self-Review"
                message="The employee has not yet started their self-review. You will be able to provide your manager review once the employee submits their self-assessment."
                statusFlow="Current: Self Review → Next: Approval Review (Manager action required)"
                icon="clock"
                variant="amber"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => (
                    <div key={question.id} className="question-card p-0">
                      <div className="flex items-start gap-4 px-6 pt-5 pb-4">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-primary">{qi + 1}</span>
                        </div>
                        <div className="flex-1 pt-0.5">
                          <p className="text-[15px] font-medium text-foreground leading-relaxed">{question.text}</p>
                        </div>
                      </div>
                      <Separator />
                      <div className="px-6 py-4">
                        <p className="text-sm text-muted-foreground italic">Waiting for employee's self-review response...</p>
                      </div>
                    </div>
                  ))}
                </SectionCard>
              ))}
            </>
          )}

          {/* SELF REVIEW: Editable for the assigned manager only */}
          {status === "Approval Review" && appraisal.canSubmitManagerReview && sections.length > 0 && (
            <>
              <StatusBanner
                title="Provide Your Approval Review"
                message="Review the employee's self-assessment and provide your evaluation with ratings and feedback."
                statusFlow="Current: Approval Review -> Next: Employee Comment (after your review)"
                icon="alert"
                variant="purple"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);

                    return (
                      <QuestionEditor
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        value={{
                          rating: ratings[question.id],
                          starScore: starScores[question.id],
                          comment: comments[question.id]
                        }}
                        onChange={handleQuestionChange}
                        ratingOptions={ratingOptions}
                        employeeSelfResponse={selfResp}
                      />
                    );
                  })}
                </SectionCard>
              ))}

              <FinalCommentCard
                title="Your Final Comment (optional)"
                description="Your overall comment on this appraisal. The employee will see it in the next step."
                comment={comments.overallComment || ''}
                onChange={(value) => handleQuestionChange('overallComment', 'comment', value)}
              />
            </>
          )}

          {status === "Approval Review" && !appraisal.canSubmitManagerReview && sections.length > 0 && (
            <>
              <StatusBanner
                title="Waiting for Assigned Reviewer"
                message="This appraisal has an assigned manager. Only that manager can submit the manager review."
                statusFlow="Current: Approval Review -> Next: Employee Comment"
                icon="clock"
                variant="amber"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={null}
                        viewMode="manager"
                      />
                    );
                  })}
                </SectionCard>
              ))}
            </>
          )}

          {/* APPROVAL REVIEW: Waiting for employee comment */}
          {/* {status === "Approval Review" && sections.length > 0 && (
            <>
              <StatusBanner
                title="Review Submitted - Waiting for Employee"
                message="Your approval review has been submitted. Waiting for the employee to provide their final comment."
                statusFlow="Current: Approval Review → Next: Employee Comment (Employee action required)"
                icon="clock"
                variant="amber"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);
                    const mgrResp = appraisal.manager_review?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={mgrResp}
                        viewMode="manager"
                      />
                    );
                  })}
                </SectionCard>
              ))}
            </>
          )} */}

          {/* EMPLOYEE COMMENT: Show employee's final comment */}
          {status === "Employee Comment" && sections.length > 0 && (
            <>
              <StatusBanner
                title="Waiting for Employee's Final Comment"
                message="Your approval review has been submitted. The employee is now reviewing your feedback and will provide their final comment."
                icon="clock"
                variant="amber"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);
                    const mgrResp = appraisal.approvalReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={mgrResp}
                        viewMode="manager"
                      />
                    );
                  })}
                </SectionCard>
              ))}

              <FinalCommentCard title="Manager's Final Comment" comment={appraisal.approvalReview?.overall_comment} />

              {/* Employee's Final Comment */}
              <Card className="border-warning/30">
                <CardHeader className="bg-warning/5 border-b">
                  <CardTitle className="text-base text-warning flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" /> Employee's Final Comment
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {appraisal.employeeComments?.comments ? (
                    <div className="p-4 rounded-lg bg-warning/10 border border-warning/20">
                      <p className="text-sm text-muted-foreground italic">"{appraisal.employeeComments.comments}"</p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No comment provided by the employee.</p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div>
                      <span className="font-semibold">Acknowledged:</span> {appraisal.employeeComments?.accepted ? 'Yes' : 'No'}
                    </div>
                    {appraisal.employeeComments?.acknowledged_at && (
                      <div>
                        <span className="font-semibold">Date:</span> {new Date(appraisal.employeeComments.acknowledged_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* ADMIN REVIEW: Waiting for admin completion */}
          {status === "Admin Review" && sections.length > 0 && (
            <>
              {appraisal.canComplete ? (
                <StatusBanner
                  title="Provide Your Admin Review"
                  message="The employee has provided their final comment. Add a comment on any question or a final comment on the overall appraisal if you'd like, then complete it. The employee will see your comments once it's completed."
                  statusFlow="Current: Admin Review -> Next: Completed (after you complete it)"
                  icon="alert"
                  variant="purple"
                />
              ) : (
                <StatusBanner
                  title="Appraisal Pending Admin Completion"
                  message="The employee has provided their final comment. The default admin is reviewing and will complete the appraisal."
                  icon="clock"
                  variant="amber"
                />
              )}

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);
                    const mgrResp = appraisal.approvalReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={mgrResp}
                        adminComment={comments[question.id] || ''}
                        onAdminCommentChange={appraisal.canComplete ? (value) => handleQuestionChange(question.id, 'comment', value) : null}
                        viewMode="manager"
                      />
                    );
                  })}
                </SectionCard>
              ))}

              <FinalCommentCard title="Manager's Final Comment" comment={appraisal.approvalReview?.overall_comment} />

    {appraisal.employeeComments?.comments && (
      <Card className="border-warning/30">
        <CardHeader className="bg-warning/5 border-b">
          <CardTitle className="text-base text-warning flex items-center gap-2">
            <AlertCircle className="h-5 w-5" /> Employee's Final Comment
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground italic">"{appraisal.employeeComments.comments}"</p>
        </CardContent>
      </Card>
    )}

              {appraisal.canComplete && (
                <FinalCommentCard
                  title="Admin's Final Comment (optional)"
                  description="Your overall comment on this appraisal. The employee will see it once the appraisal is completed."
                  comment={comments.overallComment || ''}
                  onChange={(value) => handleQuestionChange('overallComment', 'comment', value)}
                />
              )}
  </>
)}

          {/* COMPLETED: Summary view */}
          {status === "Completed" && sections.length > 0 && (
            <>
              <StatusBanner
                title="Appraisal Completed Successfully"
                message="This performance review has been completed and finalized."
                icon="check"
                variant="green"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);
                    const mgrResp = appraisal.approvalReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);
                    const adminResp = appraisal.adminReview?.responses?.find(r => r.question_id === question.id);

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={mgrResp}
                        adminResponse={adminResp}
                        viewMode="manager"
                      />
                    );
                  })}
                </SectionCard>
              ))}

              <FinalCommentCard title="Manager's Final Comment" comment={appraisal.approvalReview?.overall_comment} />
              <FinalCommentCard title="Employee's Final Comment" comment={appraisal.employeeComments?.comments} />
              <FinalCommentCard title="Admin's Final Comment" comment={appraisal.adminReview?.comments} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManagerAppraisalEditor;
